import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { CopyButton, ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type Level = 'mild' | 'moderate' | 'aggressive';

interface AiResult {
  result: string;
  meta: { provider: string; model: string; inputTokens: number; outputTokens: number; costMicrosUsd: number };
}

const LEVELS: Array<{ value: Level; label: string; hint: string }> = [
  { value: 'mild', label: 'Mild (~20%)', hint: 'Cuts filler, keeps every paragraph' },
  { value: 'moderate', label: 'Moderate (~35%)', hint: 'Balanced — typical default' },
  { value: 'aggressive', label: 'Aggressive (~50%)', hint: 'Compresses heavily, may merge sections' },
];

/**
 * Email Shortener — LLM-powered free tool. Compresses verbose drafts while
 * preserving the ask, tone, and any specific names/numbers/URLs.
 */
export function EmailShortenerPage() {
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<Level>('moderate');
  const [rateLimited, setRateLimited] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gate = useTurnstileGate();

  const shorten = useMutation<AiResult, ApiError>({
    mutationFn: () =>
      api<AiResult>('/public/tools/ai/shorten', {
        method: 'POST',
        body: { body, level },
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setRateLimited(null);
      setError(null);
    },
    onError: (e) => {
      if (e.status === 429) {
        setRateLimited(e.message);
        setError(null);
      } else {
        setError(e.message);
        setRateLimited(null);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    shorten.mutate();
  }

  const wordsIn = body.trim() ? body.trim().split(/\s+/).length : 0;
  const wordsOut = shorten.data?.result.trim() ? shorten.data.result.trim().split(/\s+/).length : 0;
  const reduction = wordsIn > 0 && wordsOut > 0 ? Math.round((1 - wordsOut / wordsIn) * 100) : null;

  const content: ToolPageContent = {
    slug: 'email-shortener',
    title: 'Email shortener',
    tagline: 'Take a verbose draft, get back a tightened version — same meaning, fewer words.',
    eyebrow: 'Free tool · AI-powered · Rate-limited per IP',
    description:
      'Free AI email shortener. Compresses a verbose email while preserving the ask, tone, and any specific names, numbers, deadlines, or URLs verbatim.',
    icon: <Icon.Wand />,
    accent: 'allow',
    intro:
      'Paste a draft that runs too long. We tighten it while preserving every specific name, number, deadline, and URL verbatim. Pick how aggressive: mild (~20%), moderate (~35%), aggressive (~50%).',
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Original draft</h2>
          <form onSubmit={onSubmit} {...gate.armProps} className="mt-5 grid gap-4">
            <Field label="Paste your email" hint="Up to ~8000 characters. Plain text or copied from your client.">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi Sarah,&#10;&#10;I hope this email finds you well…"
                rows={12}
                maxLength={8000}
                className="block w-full rounded-md border border-border bg-bg p-3 font-mono text-[12.5px] text-txt placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                autoFocus
              />
            </Field>
            <p className="text-[11.5px] text-faint">{wordsIn} words · {body.length} characters</p>

            <Field label="Reduction level">
              <div className="grid gap-2">
                {LEVELS.map((l) => (
                  <label
                    key={l.value}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-md border bg-bg px-3 py-2.5 text-[13px]',
                      level === l.value ? 'border-accent' : 'border-bordersoft hover:border-accentdeep',
                    )}
                  >
                    <input
                      type="radio"
                      checked={level === l.value}
                      onChange={() => setLevel(l.value)}
                      className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
                    />
                    <div>
                      <div className="font-medium">{l.label}</div>
                      <div className="text-[12px] text-muted">{l.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Field>

            <DeferredTurnstileWidget gate={gate} />
            <Button type="submit" variant="primary" disabled={shorten.isPending || !body.trim() || !gate.ready}>
              {shorten.isPending ? 'Shortening…' : 'Shorten email'}
            </Button>
            {error && <p className="text-[13px] text-review">{error}</p>}
            {rateLimited && (
              <p className="text-[13px] text-verify">
                {rateLimited} <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited generations.
              </p>
            )}
          </form>
        </Card>

        <Card className="p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Tightened draft</h2>
            {shorten.data && <CopyButton text={shorten.data.result} />}
          </div>
          {!shorten.data && (
            <div className="mt-4 flex h-64 items-center justify-center text-center text-[13px] text-muted">
              Paste your draft on the left, then hit Shorten.
            </div>
          )}
          {shorten.data && (
            <>
              <pre className="mt-4 whitespace-pre-wrap break-words rounded-md border border-bordersoft bg-panel2 p-4 text-[13.5px] leading-relaxed text-txt">
                {shorten.data.result}
              </pre>
              <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2 text-[11.5px] text-faint">
                <span>
                  {wordsOut} words {reduction !== null && <span className="text-allow">· {reduction}% shorter</span>}
                </span>
                <span>
                  {shorten.data.meta.provider} / {shorten.data.meta.model}
                </span>
              </div>
            </>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'Why shorter cold-email almost always wins',
      paragraphs: [
        "The single biggest predictor of reply rate on cold outreach isn't tone, personalisation, or A/B-tested subject lines — it's length. Below 100 words consistently outperforms above 200 across every dataset we've seen (Lavender, Outreach, Mailshake's public studies, our own data). The reason is simple: recipients triage their inbox by skim, and a six-line email gets read fully where a twelve-line email gets archived.",
        'The model is constrained to preserve the parts that change meaning: specific names, dates, numbers, and URLs are kept verbatim. What gets cut is the filler — "I hope this finds you well", repeated context, soft caveats, redundant pleasantries. The tone is preserved (friendly stays friendly, formal stays formal) but the words-per-point ratio shrinks.',
        'A subtle gain at the deliverability layer: shorter emails ship cleaner through filters. Spam classifiers weight text-density and structure; image-heavy and copy-heavy emails both score worse than tight, prose-only short emails. Shortening isn\'t just a UX win, it lifts inbox placement.',
      ],
    },
    faq: [
      { q: 'Will it change my meaning?', a: "No — the system prompt explicitly forbids changing instructions, actions, or factual claims. If the model can't shorten without losing meaning, it returns close to the original length." },
      { q: 'Will names, numbers, URLs survive?', a: "Yes — those are preserved verbatim by the prompt. If something specific gets mangled in practice, it's a prompt-tuning bug; tell us." },
      { q: 'What if I want it longer, not shorter?', a: "Use the AI Email Writer for a fresh draft. The shortener only goes one way." },
      { q: 'Can I see the diff between original and shortened?', a: 'Not in the free version. Paste both into a diff tool if you need a side-by-side; or just trust the prompt and re-read the output.' },
    ],
    relatedTools: [
      { slug: 'ai-email-writer', title: 'AI email writer', tagline: 'Generate a fresh draft from a goal + audience + tone.' },
      { slug: 'subject-line-generator', title: 'Subject line generator', tagline: 'Get 7 subject lines for the email you\'re sending.' },
      { slug: 'spam-words-checker', title: 'Spam words checker', tagline: 'Highlight spam-trigger words still left in the draft.' },
    ],
    ctaTitle: 'Cleaner mail, better placement',
    ctaBody:
      "Tight prose is half the win; the other half is sender reputation. SieveGuard's Domain Health add-on monitors SPF/DKIM/DMARC on every sending domain so your shorter, sharper emails actually land.",
  };

  return <ToolPageLayout content={content} />;
}

// Default export required by React Router framework-mode route modules.
export default EmailShortenerPage;
